import React, { useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ChatbotWithContext } from '@wealthlane/chatbot';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import ToastComponent from '@/components/toast';
import AuthService from '@/services/authService';
import useWealthlaneCustomerApi from '@/hooks/useWealthlaneCustomerApi';
import useInstance from '@/hooks/custom-hooks/useInstance';
import useGetCustomerUserDetails from '@/hooks/get/useGetCustomerUserDetails';
import useGetCurrentTenantInfoEnabled from '@/hooks/get/useGetCurrentTenantInfoEnabled';
import {
  axiosInstance,
  registerAuthenticatedHomeTenantId,
  unregisterAuthenticatedHomeTenantId,
} from '@/services/apiInterceptor';
import { chatbotHttpUrl, configState } from '@/components/chatbot/config';
import { getUserName } from '@/utils/global';
import { selectToken } from '@/pages/auth/authSlice';
import { selectAuthRoutesList, selectUserRoutesList } from '@/pages/configuration/routeSlice';
import { hasOrganizationSettingsPermission } from '@/utils/permission/organizationSettingsPermission';
import { getTenantId } from '@apps/customer/utils/tenantHelpers';

/**
 * Self-contained chatbot overlay. Fetches its own data
 * (user details + tenant info) and renders nothing until ready.
 * No props needed — fully autonomous inside AuthGate.
 *
 * react-query cache sharing means no duplicate requests when
 * CustomerNavbar already fetched the same data.
 */
const ChatbotOverlay: React.FC = () => {
  const navigate = useNavigate();
  const authService = new AuthService();
  const { handleRequest: handleCustomerRequest } = useWealthlaneCustomerApi();
  const { getRoleId, getCurrentCid } = useInstance();
  const cid = getCurrentCid();

  const token = useSelector(selectToken);
  const userAuthList = useSelector(selectAuthRoutesList);
  const userRouteList = useSelector(selectUserRoutesList) ?? [];
  const canViewKnowledgeSources = useMemo(
    () =>
      hasOrganizationSettingsPermission(
        { name: 'ChatBotDocument' },
        userAuthList?.auth?.grantedPolicies ?? {},
        userRouteList,
      ),
    [userAuthList?.auth?.grantedPolicies, userRouteList],
  );
  const isAuth = Boolean(token);

  const { data: userName } = useGetCustomerUserDetails(
    handleCustomerRequest,
    isAuth
  );

  const { data: currentTenantInfo } = useGetCurrentTenantInfoEnabled(
    handleCustomerRequest,
    isAuth
  );

  useLayoutEffect(() => {
    registerAuthenticatedHomeTenantId('chatbot-overlay', currentTenantInfo?.id);

    return () => {
      unregisterAuthenticatedHomeTenantId('chatbot-overlay');
    };
  }, [currentTenantInfo?.id]);

  const tenantId = getTenantId() || currentTenantInfo?.id || '';

  const resolveTenantId = (): string =>
    getTenantId() || currentTenantInfo?.id || '';

  if (!userName || !tenantId || !token) {
    return null;
  }

  return (
    <AnimatePresence initial={false} mode="wait">
      <div className="relative">
        <ChatbotWithContext
          axiosInstance={axiosInstance}
          config={configState}
          tenantIdAccessor={resolveTenantId}
          langfusePublicKey={process.env.REACT_APP_LANGFUSE_PUBLIC_KEY!}
          langfuseEndpointUrl={process.env.REACT_APP_LANGFUSE_BASE_URL!}
          accessTokenAccessor={() => authService.getToken()}
          userIdAccessor={() => String(userName?.customerUserId ?? '')}
          customerName={getUserName(userName)}
          sourceRoleId={getRoleId()}
          companyId={cid?.toString?.() ?? ''}
          httpUrl={chatbotHttpUrl}
          wsUrl={process.env.REACT_APP_CHATBOT_WEBSOCKET_API!}
          alertFunctionSuccess={(message: string) => {
            toast.success(
              <ToastComponent label="Success" message={message} />
            );
          }}
          alertFunctionError={(message: string) => {
            toast.error(
              <ToastComponent label="Error" message={message} />
            );
          }}
          navigate={navigate}
          allowMention={false}
          transcribeAudioLink={
            process.env.REACT_APP_CHATBOT_AUDIO_TRANSCRIBE_URL || ''
          }
          ingestRoute="/settings/organization-settings/chatbot-ingest"
          canViewKnowledgeSources={canViewKnowledgeSources}
          cid={cid?.toString?.() ?? ''}
        />
      </div>
    </AnimatePresence>
  );
};

export default ChatbotOverlay;